import { isMatching, match, P } from 'ts-pattern'
import type { ArtworkInfiniteScrollProps } from 'src/components/ArtworkList/BaseArtworkInfiniteScroll'
import type {
  Artwork,
  DetailedTaskParameters,
  Manga,
} from 'src/services/apollo/generated/graphql'
import { ArtworkType } from 'src/services/apollo/generated/graphql'
import { isI2vProTask, isT2I2VTask } from './task'

export function isAnimatedArtwork(artwork?: Pick<Artwork | Manga, 'type'>) {
  return artwork?.type === ArtworkType.AnimatedArtwork
}

export function isAnimatedComic<T extends Pick<Artwork | Manga, 'type'>>(
  input: T | undefined,
): boolean {
  return input?.type === ArtworkType.AnimatedComic
}

/**
 * Check if an artwork is generated via a workflow
 *
 * @param value - An artwork-like object
 */
export const isGeneratedByWorkflow = isMatching({
  extra: P.optional({
    workflowId: P.string,
  }),
})

/**
 * Will return true for both animateDiff and i2vPro tasks
 */
export const isGeneratedByI2vPro = isMatching({
  extra: P.optional({
    animatedMediaId: P.string,
  }),
})

// TODO: Rename to `createArtworkUrl`
export function getArtworkLink(
  artwork: Pick<Artwork, 'id' | 'type' | 'manga'>,
): string
export function getArtworkLink(artwork: undefined): undefined
export function getArtworkLink(
  artwork: Pick<Artwork, 'id' | 'type' | 'manga'> | undefined,
) {
  if (!artwork) return

  switch (true) {
    case isAnimatedArtwork(artwork):
      return `/video/${artwork.id}`
    case isAnimatedComic(artwork):
      return `/animated-comic/${artwork.id}`
    case artwork.manga != null:
      return `/manga/${artwork.manga.id}`
    default: {
      // use artwork id as the primary identifier
      return `/artwork/${artwork.id}`
    }
  }
}

/**
 * If user doesn't provide a title,
 * use first 33 characters of the prompts
 * @param artwork
 * @returns
 */
export function getArtworkTitle(
  artwork: Pick<Artwork, 'title' | 'prompts'>,
): string {
  if (artwork.title) return artwork.title
  return (artwork.prompts ?? '').slice(0, 33)
}

export function isArtworkArchived(artwork: Pick<Artwork, 'extra'>): boolean {
  return artwork.extra.archived === true
}

export function isArtworkAppealable(artwork?: Pick<Artwork, 'flag'>): boolean {
  return artwork?.flag?.isAppealable ?? false
}

export function shouldArtworkBeBlurred(
  artwork?: Pick<Artwork, 'flag'>,
): boolean {
  return artwork?.flag?.shouldBlur ?? false
}

export enum ArtworkTypeFilterValue {
  All = 'all',
  Image = 'image',
  Animated = 'animated',
}

export interface ArtworkFilterValue {
  type: ArtworkTypeFilterValue
  hidePrompts?: boolean
}

/**
 * Converts ArtworkFilterValue to props for BaseArtworkInfiniteScroll
 * Returns ArtworkInfiniteScrollProps which will be merged into ListArtworksQueryVariables
 *
 * @param filter - The artwork filter configuration
 * @returns Props that can be spread into BaseArtworkInfiniteScroll
 */
export function getArtworkQueryArgsByArtworkFilter(
  filter: ArtworkFilterValue,
): ArtworkInfiniteScrollProps {
  const baseArgs = match(filter.type)
    .with(ArtworkTypeFilterValue.Image, () => ({
      type: ArtworkType.Default,
    }))
    .with(ArtworkTypeFilterValue.Animated, () => ({
      type: ArtworkType.AnimatedArtwork,
      i2vPro: true,
    }))
    .otherwise(() => ({}))

  return {
    ...baseArgs,
    ...(filter.hidePrompts !== undefined && {
      extraArgs: { hidePrompts: filter.hidePrompts },
    }),
  }
}

/**
 * "Use as reference"
 * @param artwork
 * @param taskParamerters
 * @returns
 */
export function isArtworkAbleToBeReferenced(
  artwork?: Pick<Artwork, 'extra'>,
  taskParamerters?: DetailedTaskParameters,
) {
  return (
    !isGeneratedByWorkflow(artwork) &&
    !isI2vProTask(taskParamerters) &&
    !isT2I2VTask(taskParamerters)
  )
}

/**
 * Calculate object-position to center the face in a container
 * while ensuring no blank space is shown.
 *
 * @param faceCenter - Relative coordinates (0-1) of the face center
 * @param imageWidth - Original image width
 * @param imageHeight - Original image height
 * @param containerAspect - Container aspect ratio (width/height), defaults to 1 (square)
 * @returns CSS object-position string, or undefined if no face center
 */
export function getFaceCenterObjectPosition(
  faceCenter: { x: number; y: number } | undefined | null,
  imageWidth: number | undefined | null,
  imageHeight: number | undefined | null,
  containerAspect = 1,
): string | undefined {
  if (!faceCenter) return undefined

  // If we don't have image dimensions, use face position directly
  if (!imageWidth || !imageHeight) {
    return `${faceCenter.x * 100}% ${faceCenter.y * 100}%`
  }

  const imageAspect = imageWidth / imageHeight

  let posX: number
  let posY: number

  if (imageAspect >= containerAspect) {
    // Landscape or square image in square container
    // Full height visible, width is cropped - X position controls view
    const visibleWidthRatio = containerAspect / imageAspect

    if (visibleWidthRatio >= 0.99) {
      // Nearly square - use face position directly
      posX = faceCenter.x * 100
    } else {
      // Calculate position to center the face horizontally
      const idealStartX = faceCenter.x - visibleWidthRatio / 2
      posX = (idealStartX / (1 - visibleWidthRatio)) * 100
      posX = Math.max(0, Math.min(100, posX))
    }

    // Y: full height visible, use face Y (minimal effect)
    posY = faceCenter.y * 100
  } else {
    // Portrait image in square container
    // Full width visible, height is cropped - Y position controls view
    const visibleHeightRatio = imageAspect / containerAspect

    // Calculate position to center the face vertically
    const idealStartY = faceCenter.y - visibleHeightRatio / 2
    posY = (idealStartY / (1 - visibleHeightRatio)) * 100
    posY = Math.max(0, Math.min(100, posY))

    // X: full width visible, use face X (minimal effect)
    posX = faceCenter.x * 100
  }

  return `${posX}% ${posY}%`
}
